import type { BackendObjects, Framework } from "@hatch/shared";
import { hasRealtime } from "@hatch/shared";
import { useEffect, useRef, useState } from "react";
import { headlineObjects, otherObjects } from "../../lib/backendObjects.js";
import { frameSandbox, frameUrl, isRenderableSiteUrl } from "../../lib/frameUrl.js";
import { FRAMEWORK_OPTIONS } from "../../lib/frameworks.js";
import { describeElapsed } from "../../lib/labels.js";
import { usePreviewFrame } from "../../lib/usePreviewFrame.js";
import { useScrollToTop } from "../../lib/useScrollToTop.js";
import type { Step } from "../../lib/viewModel.js";
import { HANDOFF_COPY, backendHandoffPath } from "../../lib/xanoHandoff.js";
import { BuildLog } from "./BuildLog.js";
import { Header, XanoScreen } from "./XanoScreen.js";

/**
 * The reveal.
 *
 * Reached only once the site has actually served to this browser — the gate is
 * in `BuildView`, because it decides which screen shows rather than what this
 * one contains. By the time anything here renders, the address below answered a
 * real request moments ago.
 *
 * This is the one screen in the skin that SCROLLS rather than filling the
 * viewport, and the distinction is not cosmetic. The build screen must be
 * exactly viewport height because its log grows for minutes and would otherwise
 * grow the document with it. Nothing here grows: the moment this renders, the
 * build is over and every number on it is final.
 *
 * The page is now two NUMBERED DELIVERABLES rather than a stack of panels, and
 * that is the whole shape of it. A visitor at a booth is being handed two
 * things — a site that is on the internet, and a backend they can read — and
 * the old layout said so only by ordering: address, preview, a diagram, a card.
 * Numbering them, giving each a headline that states what it IS, and colouring
 * them apart (blue for the site, violet for the backend) makes a passer-by able
 * to take in the claim from further away than they can read any of the copy.
 *
 * Below the two sections, in order: the preview, the finished log, and the way
 * out. All three are evidence rather than headline — someone who wants to look
 * closer scrolls, and someone who wants the two claims never has to.
 */

export function DoneView({
  siteUrl,
  prompt,
  sessionId,
  steps,
  fileCount,
  buildMs,
  framework,
  hasBackend,
  objects,
  sitePages,
  boothCode,
  onRestart,
}: {
  siteUrl: string;
  prompt: string | null;
  sessionId: string;
  steps: Step[];
  fileCount: number;
  buildMs: number | null;
  /** Which frontend was built, for the chip that says so. */
  framework?: Framework;
  /** Whether this build has a Xano backend worth offering. See lib/xanoHandoff. */
  hasBackend: boolean;
  /**
   * Every primitive the build registered, when Hatch could establish it — not
   * just tables and endpoints. Each key is independently optional and an absent
   * one renders as nothing at all; see `SessionSnapshot`, where the
   * absent-means-unknown rule is set out, and lib/backendObjects for why a real
   * zero renders as nothing too.
   */
  objects?: BackendObjects | null;
  /** Documents the deploy published, when it reported a count. */
  sitePages?: number | null;
  /**
   * The code this visitor reads aloud at the booth. Null for a website build,
   * which is most of them — the panel does not render at all rather than
   * rendering empty.
   */
  boothCode?: string | null;
  onRestart: () => void;
}) {
  // This screen is the only one in a run that scrolls, and it inherits whatever
  // offset the document was left at — see lib/useScrollToTop. Arriving halfway
  // down the reveal is the one first impression it cannot afford.
  useScrollToTop();

  return (
    <XanoScreen>
      {/*
        The header's right-hand slot is deliberately empty here. It used to hold
        a "Live" pill, which the delivery band below now says at more weight and
        with the numbers attached — two claims of the same fact within 60px of
        each other is one of them being decoration.
      */}
      <Header prompt={prompt} />

      {/*
        No progress rail. It is the build screen's chrome and it followed the
        reveal here out of habit — six settled steps saying "every phase
        completed", which the delivery band below now says with the numbers
        attached. Keeping it also cost the page its structure: the rail numbers
        its phases 01-06 and the two sections number themselves 01 and 02, so
        within 400px the same idiom meant two different things and the second
        one read as a continuation of the first.
      */}
      <main className="mx-auto flex w-full max-w-[900px] flex-1 flex-col px-4 pb-10 sm:px-6">
        <DeliveredBand fileCount={fileCount} stepCount={steps.length} buildMs={buildMs} />

        {/*
          Above both sections, and on BOTH paths.

          The booth slot leads because of who it is for: someone texted a build
          already holds the link on their phone, and what they cannot get from
          it is the six characters they have to carry across a hall to a
          stranger. The invitation moved up with it, one step removed — most
          builds at the show are website builds driven on the monitor, and their
          audience is a person walking past who never scrolls at all.

          Exactly one of the two renders. They say the same thing to different
          people — a code is an instruction, an invitation is an offer — and
          showing both would be the screen asking twice.
        */}
        <div className="pt-4">
          {boothCode ? <BoothCode code={boothCode} /> : <BoothInvite />}
        </div>

        <Deliverable
          index="01"
          rail="Frontend"
          accent="blue"
          heading="Your app, live on the internet"
          sub="Deployed and verified. Nothing to install, nothing to run locally."
        >
          <AddressBar siteUrl={siteUrl} />
          <SiteChips
            sitePages={sitePages ?? null}
            framework={framework}
            objects={objects ?? null}
          />
        </Deliverable>

        {hasBackend && (
          <Deliverable
            index="02"
            rail="Backend"
            accent="violet"
            heading={BACKEND_HEADING}
            sub={HANDOFF_COPY.scope}
            action={
              <a
                href={backendHandoffPath(sessionId)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 shrink-0 items-center rounded-lg bg-deck-violet-lift px-5 text-[14px] font-semibold text-deck transition-colors hover:bg-deck-violet-hi"
              >
                {HANDOFF_COPY.action} &rarr;
              </a>
            }
          >
            <BackendCounts objects={objects ?? null} />
          </Deliverable>
        )}

        {/*
          Evidence, below the claims. The preview proves the address serves
          something; the log proves it was built rather than fetched. Neither is
          the headline, and both used to sit where the headline should be.
        */}
        <div className="mt-6 flex flex-col gap-3">
          <SitePreview siteUrl={siteUrl} />

          {/*
            The build is over by construction — the canvas only reaches
            `framing` on a terminal event carrying a site URL — so the feed is a
            finished artifact rather than something still arriving. Bounded and
            scrolling inside itself, because it is the longest thing on the page.
          */}
          <div className="flex h-[clamp(220px,38vh,360px)] flex-col">
            <BuildLog steps={steps} isTerminal fileCount={fileCount} />
          </div>
        </div>

        <div className="mt-5 flex flex-col items-center gap-2.5 text-center">
          <button
            type="button"
            onClick={onRestart}
            className="h-11 cursor-pointer rounded-lg border border-deck-line px-5 text-[14px] font-medium text-deck-text-soft transition-colors hover:border-deck-blue hover:text-deck-text"
          >
            Build another
          </button>
          <p className="text-[12px] text-deck-text-faint">
            This build runs on ephemeral infrastructure and expires after about an hour.
          </p>
        </div>
      </main>
    </XanoScreen>
  );
}

/**
 * The heading over the backend half.
 *
 * Not `HANDOFF_COPY.heading` ("Your backend is real too"), and the divergence is
 * deliberate rather than drift. That sentence answers a doubt — it assumes the
 * reader suspects the backend is a mock — which is the right line for a card
 * bolted under a preview in another skin's colours. Here the backend has its own
 * numbered half of the page, so the doubt is already answered by the layout, and
 * the heading can do the more useful job of saying what is being offered.
 *
 * `HANDOFF_COPY.scope` is still used verbatim below it, because that one is a
 * claim about the world: it describes what `api/src/xano/impersonate.ts` mints,
 * and a skin that drifted into promising more would be lying about a security
 * property rather than choosing a tone.
 */
const BACKEND_HEADING = "The whole backend, yours to read";

/**
 * The line across the top: what happened, in how many pieces, in how long.
 *
 * Three numbers a visitor never asked for and every one of them earns its place,
 * because together they are the answer to the only sceptical question this
 * screen gets asked at a booth — "did it really just do that?". A count of
 * artifacts and a count of steps are the shape of work; a duration in minutes is
 * the claim that lands. None of them is invented: the artifacts are the files
 * the stream saw written, the steps are the feed's own rows, and the duration is
 * the difference of two server timestamps.
 *
 * The green is `deck-live`, which in this skin means completion and nothing
 * else — never the accent, which means here and now. A finished build is the one
 * thing on this page that is unambiguously over.
 *
 * The build number used to sit in the right-hand corner, set like a serial
 * number. It was the visitor's booth code wearing a second costume, and the
 * panel immediately below already shows those six characters at four times the
 * size with the sentence that says what to do with them. Two printings of one
 * code within a hundred pixels does not make it twice as findable — it makes
 * the reader check whether they are the same string.
 */
function DeliveredBand({
  fileCount,
  stepCount,
  buildMs,
}: {
  fileCount: number;
  stepCount: number;
  buildMs: number | null;
}) {
  // Each part is dropped rather than zeroed when there is nothing behind it.
  // "0 artifacts" on a screen whose whole job is to prove something was built
  // is the one reading this band cannot survive.
  const facts = [
    fileCount > 0 ? `${String(fileCount)} ${fileCount === 1 ? "artifact" : "artifacts"}` : null,
    stepCount > 0 ? `${String(stepCount)} ${stepCount === 1 ? "step" : "steps"}` : null,
    buildMs !== null ? describeElapsed(buildMs) : null,
  ].filter((part): part is string => part !== null);

  return (
    <section
      aria-label="Build summary"
      className="-mx-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-dashed border-deck-line px-4 py-3 sm:-mx-6 sm:px-6"
      // Anchored to the left rather than a flat wash across the bar. A single
      // tint over the full width sits on top of XanoScreen's blue backdrop and
      // the two mix to teal — a colour this skin does not have and which reads
      // as a fourth state beside live / halt / accent. Fading it out by the
      // midpoint keeps the green next to the word it qualifies and lets the
      // backdrop be the backdrop.
      style={{
        background:
          "linear-gradient(90deg, var(--color-deck-live-soft) 0%, transparent 55%)",
      }}
    >
      <span className="flex shrink-0 items-center gap-2">
        <span aria-hidden className="size-2 rounded-full bg-deck-live" />
        <span className="text-[17px] font-bold tracking-[-0.01em] sm:text-[19px]">Delivered</span>
      </span>

      {facts.length > 0 && (
        <span className="font-deck-mono text-[10.5px] tracking-[0.14em] text-deck-text-faint uppercase">
          {facts.join(" · ")}
        </span>
      )}
    </section>
  );
}

/**
 * One numbered half of the page.
 *
 * The rail — a big translucent numeral and the section's name set vertically —
 * is decoration in the strict sense that removing it loses no information, and
 * it is kept because it does the one thing the copy cannot: it makes the page
 * legible as TWO THINGS from across a room, before any word on it is readable.
 * That is the distance a booth screen is actually viewed from.
 *
 * It is `aria-hidden` and hidden below `sm` for the same reason: it repeats the
 * heading beside it, so a screen reader announcing it would read every section
 * twice, and a phone has no width to spend on a column that says nothing new.
 * The heading is the real landmark and carries the accessible name.
 */
function Deliverable({
  index,
  rail,
  accent,
  heading,
  sub,
  action,
  children,
}: {
  index: string;
  rail: string;
  accent: "blue" | "violet";
  heading: string;
  sub: string;
  /** The section's one primary control, when it has one. */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const tone = ACCENT[accent];
  return (
    <section aria-label={heading} className="flex border-b border-dashed border-deck-line py-6">
      <div
        aria-hidden
        className="mr-5 hidden w-14 shrink-0 flex-col items-center gap-3 border-r border-dashed border-deck-line pr-4 sm:flex"
      >
        <span className={`font-deck-mono text-[26px] leading-none font-bold ${tone.numeral}`}>
          {index}
        </span>
        <span
          className="font-deck-mono text-[9.5px] tracking-[0.22em] text-deck-text-faint uppercase"
          style={{ writingMode: "vertical-rl" }}
        >
          {rail}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3.5">
        {/*
          Stacked on a phone, side by side above it. The action cannot share a
          row at 390px: "The whole backend, yours to read" is squeezed into
          three lines to make room for a button, which trades the sentence that
          does the selling for the control it sells. Below the copy it also
          reads in the order it is used.
        */}
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h2 className="text-[21px] font-bold tracking-[-0.01em] text-balance sm:text-[26px]">
              {heading}
            </h2>
            <p className="mt-1.5 text-[13.5px] leading-[1.5] text-pretty text-deck-text-soft">
              {sub}
            </p>
          </div>
          {action}
        </div>
        {children}
      </div>
    </section>
  );
}

/**
 * Which colour each half is told apart by.
 *
 * A record rather than an interpolated class name, because Tailwind resolves
 * classes statically: `text-deck-${accent}-hi` produces no CSS at all and fails
 * as a section that is simply the wrong colour, which nothing tests and nobody
 * notices until the booth.
 */
const ACCENT = {
  blue: { numeral: "text-deck-blue-hi" },
  violet: { numeral: "text-deck-violet-hi" },
} as const;

/**
 * The facts about the site, as chips.
 *
 * Every one is dropped when Hatch does not know it, which is why this is a
 * filtered list rather than four elements with conditions hung off them — the
 * row has to look composed with one chip in it as well as with four.
 *
 * The health chip is the only one that is not read off a count, and it is the
 * one worth being careful about: it says the site answered, and it is true by
 * construction rather than by assertion. `BuildView` will not render this
 * component until `useSiteReady` has both probed the address server-side and
 * rehearsed the frame's own requests from this browser. If that gate ever
 * loosens, this chip becomes a claim rather than a report and must move or go.
 */
function SiteChips({
  sitePages,
  framework,
  objects,
}: {
  sitePages: number | null;
  framework: Framework | undefined;
  objects: BackendObjects | null;
}) {
  const frameworkLabel = FRAMEWORK_OPTIONS.find((o) => o.value === framework)?.label ?? null;

  const chips = [
    sitePages !== null && sitePages > 0
      ? `${String(sitePages)} ${sitePages === 1 ? "page" : "pages"}`
      : null,
    frameworkLabel,
    // Named on the FRONT-end half on purpose: a socket is something the page
    // does, and this is the chip that tells a visitor their chat room is a chat
    // room rather than a list that reloads.
    hasRealtime(objects) ? "Realtime stream" : null,
  ].filter((chip): chip is string => chip !== null);

  return (
    <ul className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        <li
          key={chip}
          className="rounded-full border border-deck-line px-3.5 py-1.5 font-deck-mono text-[10.5px] tracking-[0.14em] text-deck-text-soft uppercase"
        >
          {chip}
        </li>
      ))}
      <li className="rounded-full border border-deck-live/50 px-3.5 py-1.5 font-deck-mono text-[10.5px] tracking-[0.14em] text-deck-live uppercase">
        Health check passed
      </li>
    </ul>
  );
}

/**
 * What the build put in the tenant.
 *
 * Three tiles and a line, and the split is the design decision. Xano can author
 * seventeen kinds of thing and a build may ship a dozen of them, but a stranger
 * reading a booth monitor for two seconds can hold three numbers. Tables, the
 * endpoints over them, and the logic they share are what "a backend" means to
 * someone who has never opened Xano; everything else is real, secondary, and
 * belongs on one line where it reads as depth rather than as a spreadsheet.
 *
 * Nothing renders a zero, in either place, and the two absences are different:
 * a kind Hatch could not establish carries no number at all, and a kind the
 * build genuinely has none of carries a real `0` that is filtered out in
 * lib/backendObjects. Both come out as silence here, because "0 triggers"
 * beside "3 tables" reads as a broken feature rather than as a site that never
 * needed one.
 */
function BackendCounts({ objects }: { objects: BackendObjects | null }) {
  const headline = headlineObjects(objects);
  const rest = otherObjects(objects);

  // A build whose source Hatch could not read still has a real backend and a
  // real door into it. The section keeps its heading and its button and simply
  // says nothing it cannot stand behind.
  if (headline.length === 0 && rest.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {headline.length > 0 && (
        // Three across at every width, including 390px. Stacked full-width they
        // are three cards a phone has to scroll past to reach the evidence
        // below, and the numbers are the one thing on this page that reads at a
        // glance — a row of them is the picture, a column of them is a list.
        <ul className="grid grid-cols-3 gap-2 sm:gap-2.5">
          {headline.map((object) => (
            <li
              key={object.kind}
              className="rounded-xl border border-deck-line bg-deck-raise px-3 py-3 sm:px-4 sm:py-3.5"
            >
              <p className="text-[26px] leading-none font-bold tabular-nums text-deck-text sm:text-[30px]">
                {object.count}
              </p>
              <p className="mt-2 font-deck-mono text-[9.5px] leading-[1.35] tracking-[0.12em] text-deck-text-faint uppercase sm:text-[10px] sm:tracking-[0.16em]">
                {object.noun}
              </p>
            </li>
          ))}
        </ul>
      )}

      {rest.length > 0 && (
        <p className="font-deck-mono text-[10.5px] leading-[1.7] tracking-[0.12em] text-deck-text-faint uppercase">
          {/* Middle dots rather than commas: this is a readout, and a
              comma-joined list of counts reads as a sentence that trails off. */}
          {rest.map((object) => object.label).join(" · ")}
        </p>
      )}
    </div>
  );
}

/**
 * The address, with the two controls that spend it.
 *
 * Not conditional on anything, because a cross-origin frame that rendered
 * nothing looks exactly like one that rendered — so the thing the visitor
 * actually leaves with cannot depend on the preview having worked.
 *
 * Copy and Open are both here and they are for different people. Copying
 * matters most on a phone at someone else's booth: nobody retypes a generated
 * subdomain, and the tab they are in is not one they will still have tomorrow.
 * Open matters most on the booth monitor, where the whole point of the moment is
 * that a stranger watches the site come up.
 */
function AddressBar({ siteUrl }: { siteUrl: string }) {
  // Held so a second press restarts the window instead of inheriting the first
  // press's deadline. Cleared on unmount so the timer is not still holding a
  // setter for a tree that went away when "Build another" was pressed.
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [copied, setCopied] = useState(false);
  useEffect(() => () => clearTimeout(timer.current), []);

  const followable = isRenderableSiteUrl(siteUrl);

  async function copy() {
    try {
      await navigator.clipboard.writeText(siteUrl);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access can be denied; the address is selectable either way.
    }
  }

  return (
    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-stretch">
      <div className="flex min-w-0 flex-1 items-stretch overflow-hidden rounded-lg border border-deck-line bg-deck-raise">
        <span
          aria-hidden
          className="hidden shrink-0 items-center pl-3.5 font-deck-mono text-[10px] tracking-[0.16em] text-deck-text-faint uppercase sm:flex"
        >
          URL
        </span>
        <div className="flex min-w-0 flex-1 items-center px-3">
          {/* An address Hatch will not follow is still an address worth showing —
              as text. See `isRenderableSiteUrl`: the URL is scraped from the
              deploy's own stdout inside the agent's workspace, so it is downstream
              of a model, and `javascript:` parses perfectly well. */}
          {followable ? (
            <a
              href={siteUrl}
              target="_blank"
              rel="noreferrer"
              title={siteUrl}
              className="flex min-h-11 min-w-0 items-center truncate font-deck-mono text-[12.5px] text-deck-blue-hi transition-colors hover:text-deck-text"
            >
              {siteUrl}
            </a>
          ) : (
            <span
              className="flex min-h-11 min-w-0 items-center truncate font-deck-mono text-[12.5px] text-deck-text-faint"
              title={siteUrl}
            >
              {siteUrl}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={copy}
          className="h-11 shrink-0 cursor-pointer self-center border-l border-deck-line px-4 text-[13px] font-medium text-deck-text-soft transition-colors hover:text-deck-text"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {followable && (
        <a
          href={siteUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-11 shrink-0 items-center justify-center rounded-lg bg-deck-blue-lift px-6 text-[14px] font-semibold text-deck transition-colors hover:bg-deck-blue-hi"
        >
          Open &#8599;
        </a>
      )}
    </div>
  );
}

/**
 * The site itself, at a fixed height rather than filling what is left.
 *
 * Below both numbered sections now, which is a demotion and an intentional one.
 * It is the slowest thing on the page to arrive, it is the one element that can
 * silently show nothing, and it says less than the address above it does — so it
 * is evidence for someone who wants to look, not the thing the screen leads
 * with. A viewport-relative clamp keeps it big enough to recognise the site and
 * small enough that the visitor scrolls past it rather than into it.
 */
function SitePreview({ siteUrl }: { siteUrl: string }) {
  const { loadKey, reloading, reload } = usePreviewFrame(siteUrl);
  const followable = isRenderableSiteUrl(siteUrl);

  return (
    <section className="overflow-hidden rounded-xl border border-deck-line bg-deck-raise">
      <div className="flex items-center justify-between gap-3 border-b border-deck-line px-3 py-1.5">
        <span className="font-deck-mono text-[10px] tracking-[0.16em] text-deck-text-faint uppercase">
          Preview
        </span>
        {followable && (
          <button
            type="button"
            onClick={() => void reload()}
            disabled={reloading}
            className="-mr-2 flex h-11 cursor-pointer items-center px-2 font-deck-mono text-[10px] tracking-[0.12em] text-deck-text-faint uppercase transition-colors hover:text-deck-blue-hi disabled:opacity-60"
          >
            {reloading ? "Reloading" : "Reload"}
          </button>
        )}
      </div>

      <div className="h-[clamp(220px,34vh,380px)] bg-white">
        {followable ? (
          <iframe
            src={frameUrl(siteUrl, loadKey)}
            title="Preview of your site"
            // Generated by an agent from a user prompt: treated as untrusted
            // third-party content, and given no access back to Hatch. The grant
            // is computed rather than literal — see `frameSandbox`, which adds
            // `allow-same-origin` only for a site that is provably not us,
            // because without it a page that touches localStorage dies before
            // it mounts and previews as a white rectangle.
            sandbox={frameSandbox(siteUrl)}
            referrerPolicy="no-referrer"
            className="size-full border-0 bg-white"
          />
        ) : (
          <div className="flex size-full items-center justify-center px-8 text-center">
            <p className="text-[14px] text-deck-text-faint">
              The builder reported an address Xano will not open.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * The invitation, for a build with no code behind it — which is most of them.
 *
 * Shaped as BoothCode's sibling on purpose: same shell, same slot, same centred
 * treatment, minus the six characters. That is what lets one substitute for the
 * other at the top of the screen without the layout moving, and it is why this
 * is a component rather than the bare line it replaced.
 *
 * It carries the offer that BoothCode's second paragraph carries, in the same
 * words, because it is the same offer — the only difference is that this
 * visitor has nothing to bring and can simply walk over. The eyebrow leads with
 * the location rather than the pitch: a passer-by reads two lines at most, and
 * "Booth 606" is the one that has to survive being the only line they read.
 */
function BoothInvite() {
  return (
    <section className="rounded-xl border border-deck-line bg-deck-raise px-4 py-3.5 text-center">
      <p className="font-deck-mono text-[10.5px] tracking-[0.16em] text-deck-blue-hi uppercase">
        AI TechWorld 2026 &middot; Booth 606
      </p>
      <p className="mt-2 text-[12.5px] leading-[1.5] text-pretty text-deck-text-soft">
        Come see us and we&rsquo;ll pull your app up, demo it live, and show you
        how Xano builds AI apps your team can understand and maintain.
      </p>
    </section>
  );
}

/**
 * The code the visitor brings to Booth 606.
 *
 * The one place this code appears. It briefly also sat in the delivery band's
 * corner as a build number, and that was the same string twice within a hundred
 * pixels — which does not make a code twice as findable, it makes the reader
 * stop to check whether the two match. Grouping the characters with the
 * sentence is what makes them legible as something to act on rather than one
 * more identifier on a screen already full of them.
 *
 * Rendered without the SMS's hyphen. That separator exists so the code can be
 * dictated over a noise floor; here it is being read off a screen, where wide
 * tracking does the same job without inviting somebody to type the hyphen into
 * a field that does not want it. `normalizeCode` on the API side accepts both
 * anyway, which is why the two presentations are free to differ.
 *
 * Selectable, not copyable: there is no clipboard affordance because the
 * destination is a person, not a field. Six characters is also short enough to
 * hold in your head across a hall, which is the entire reason it is six.
 */
function BoothCode({ code }: { code: string }) {
  return (
    <section className="rounded-xl border border-deck-line bg-deck-raise px-4 py-3.5 text-center">
      <p className="font-deck-mono text-[10px] tracking-[0.16em] text-deck-text-faint uppercase">
        Your booth code
      </p>
      <p className="mt-1.5 font-deck-mono text-[26px] leading-none font-bold tracking-[0.22em] text-deck-blue-hi sm:text-[30px]">
        {code}
      </p>
      <p className="mt-2.5 text-[12.5px] leading-[1.5] text-pretty text-deck-text-soft">
        Show this at Booth 606 and we&rsquo;ll pull your app up, demo it live,
        and show you how Xano builds AI apps your team can understand and
        maintain.
      </p>
    </section>
  );
}
